import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {Alert, Autocomplete, Divider, MenuItem, Select, Stack, Switch} from "@mui/material";
import {useEffect, useMemo, useState} from "react";
import {useMutation, useQuery} from "react-query";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {debounce} from "lodash";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import {useTranslation} from "react-i18next";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {
    OhipConfig,
    OhipCredentials,
    OhipDataProcessingRules
} from "crqlar-react-common/dist/models/integrations/ohip/ohip.config";
import {
    FullSyncConfig,
    PeriodicSyncConfig,
    PollerConfig
} from "crqlar-react-common/dist/models/integrations/pollerConfig.model";
import {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import {LoadingButton} from "@mui/lab";
import {MbscCalendarEvent} from "@mobiscroll/react";
import {OhipService} from "crqlar-react-common/dist/services/integrations/ohip/ohip.service";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {enqueueSnackbar} from "notistack";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

interface Props {
    ohipConfigData: UseEntityData<OhipConfig>
}

const OhipConfigForm = ({ohipConfigData}: Props) => {

    const {t} = useTranslation()
    const sectors: Sector[] = useAllSectors()

    const ohipConfig = ohipConfigData.item
    const nameMissing = isRequiredTextMissing(ohipConfigData, ohipConfig.name)
    const setOhipConfig = ohipConfigData.setItem

    const readOnlyUser = useReadOnly()

    const ageCategories = useAllAgeCategories()

    //region Handlers

    const checkConnection = useMutation(
        ['checkOhipConfig', ohipConfig.credentials],
        async (event: MbscCalendarEvent) => {
            let success = false
            try {
                if (ohipConfig.credentials) {
                    await OhipService.checkConnection(ohipConfig);
                    success = true
                }
            } catch (error) {
            }

            enqueueSnackbar(
                success ? t('Connection successful') : t('Connection failed'),
                {
                    variant: success ? 'success' : 'error',
                    anchorOrigin: {vertical: 'bottom', horizontal: 'center'},
                }
            )
        }
    )

    const updateField = (field: keyof OhipConfig, value: any) => {
        setOhipConfig({...ohipConfig, [field]: value})
    }

    const updateCredentialsField = (field: keyof OhipCredentials, value: any) => {
        setOhipConfig({
            ...ohipConfig,
            credentials: {
                ...ohipConfig.credentials,
                [field]: value
            }
        })
    }

    const updatePollerConfig = (field: keyof PollerConfig, value: any) => {
        setOhipConfig({
            ...ohipConfig,
            pollerConfig: {
                ...ohipConfig.pollerConfig,
                [field]: value
            }
        })
    }

    const updateFullSyncConfig = (field: keyof FullSyncConfig, value: any) => {
        updatePollerConfig('fullSync', {
            ...ohipConfig.pollerConfig?.fullSync,
            [field]: value
        })
    }

    const updateDailyConfig = (field: keyof PeriodicSyncConfig, value: any) => {
        updatePollerConfig('daySync', {
            ...ohipConfig.pollerConfig?.daySync,
            [field]: value
        })
    }

    const updateQuickConfig = (field: keyof PeriodicSyncConfig, value: any) => {
        updatePollerConfig('quickSync', {
            ...ohipConfig.pollerConfig?.quickSync,
            [field]: value
        })
    }

    const updateDataProcessingRules = (field: keyof OhipDataProcessingRules, value: any) => {
        updateField('dataProcessingRules', {
            ...ohipConfig.dataProcessingRules,
            [field]: value
        })
    }

    const [resourceDebouncedQuery, setResourceDebouncedQuery] = useState('')

    const searchResourcesQuery = useQuery(['allResources', resourceDebouncedQuery], async () => {
        const response = await ResourceService.getAllResources({
            name: resourceDebouncedQuery ? [resourceDebouncedQuery] : undefined,
            limit: {
                offset: 0,
                limit: 50,
                sort: [{field: "firstName", direction: "ASC"}]
            }
        })

        return response.content || []
    })

    const resourceDebouncedSearch = useMemo(() =>
            debounce((query) => {
                setResourceDebouncedQuery(query)
            }, 500),
        []
    );

    useEffect(() => {
        return () => {
            resourceDebouncedSearch.cancel();
        };
    }, [resourceDebouncedSearch])

    //endregion Handlers

    //region UI

    const credentialsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Integration name')}
                    tooltip={t('Internal label for identifying this OHIP integration configuration.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={ohipConfig.name || ''}
                        error={nameMissing}
                        fullWidth
                        onChange={e => updateField('name', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Gateway URL')}
                    tooltip={t('OHIP gateway endpoint used for API requests.')}
                >
                    <TextField
                        id={'url'}
                        size={'small'}
                        value={ohipConfig.credentials?.url || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('url', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Client ID')}
                    tooltip={t('OHIP client ID used to authorize API requests.')}
                >
                    <TextField
                        id={'client-id'}
                        size={'small'}
                        value={ohipConfig.credentials?.clientId || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('clientId', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Client secret')}
                    tooltip={t('OHIP client secret paired with the client ID.')}
                >
                    <TextField
                        id={'client-secret'}
                        size={'small'}
                        value={ohipConfig.credentials?.clientSecret || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('clientSecret', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Hotel ID')}
                    tooltip={t('Hotel identifier assigned by Oracle Hospitality.')}
                >
                    <TextField
                        id={'url'}
                        size={'small'}
                        value={ohipConfig.credentials?.hotelId || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('hotelId', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Enterprise ID')}
                    tooltip={t('Enterprise identifier assigned by Oracle Hospitality.')}
                >
                    <TextField
                        id={'enterprise-id'}
                        size={'small'}
                        value={ohipConfig.credentials?.enterpriseId || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('enterpriseId', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Chain code')}
                    tooltip={t('Chain code used by OHIP to identify the hotel group.')}
                >
                    <TextField
                        id={'url'}
                        size={'small'}
                        value={ohipConfig.credentials?.chainCode || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('chainCode', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Grant type')}
                    tooltip={t('OAuth grant type used when requesting OHIP access tokens.')}
                >
                    <Select
                        value={ohipConfig.credentials?.grantType || ''}
                        onChange={(e) => updateCredentialsField('grantType', e.target.value)}
                        fullWidth
                        size={'small'}
                    >
                        {
                            ['password', 'client_credentials'].map((type, index) => (
                                <MenuItem
                                    key={index}
                                    value={type}
                                    id={`grant-type-${type}`}
                                >
                                    {type}
                                </MenuItem>
                            ))
                        }
                    </Select>
                </Property>

                {
                    ohipConfig.credentials?.grantType === 'password' ? (
                        <>
                            <Property
                                title={t('Username')}
                                tooltip={t('OHIP username used when the password grant type is selected.')}
                            >
                                <TextField
                                    id={'username'}
                                    size={'small'}
                                    value={ohipConfig.credentials?.username || ''}
                                    fullWidth
                                    onChange={e => updateCredentialsField('username', e.target.value)}
                                />
                            </Property>

                            <Property
                                title={t('Password')}
                                tooltip={t('OHIP password used when the password grant type is selected.')}
                            >
                                <TextField
                                    id={'password'}
                                    size={'small'}
                                    value={ohipConfig.credentials?.password || ''}
                                    fullWidth
                                    type={'password'}
                                    onChange={e => updateCredentialsField('password', e.target.value)}
                                />
                            </Property>
                        </>
                    ) : (
                        <Property
                            title={t('Scope')}
                            tooltip={t('OAuth scopes requested when client credentials are used.')}
                        >
                            <TextField
                                id={'scope'}
                                size={'small'}
                                value={ohipConfig.credentials?.scope || ''}
                                fullWidth
                                onChange={e => updateCredentialsField('scope', e.target.value)}
                            />
                        </Property>
                    )
                }

                <Stack direction={'row'} justifyContent={'flex-start'}>
                    <LoadingButton
                        disabled={readOnlyUser}
                        loading={checkConnection.isLoading}
                        onClick={checkConnection.mutate}
                        variant={'outlined'}
                    >
                        {t('Test connection')}
                    </LoadingButton>
                </Stack>
            </Stack>
        </Stack>
    )

    const pollerConfig = (
        <AdditionalProperties title={t('Data sync setup')} variant={'outlined'}>

            <Typography textAlign={'start'} sx={{mt: 1}}>
                {t('Full sync')}
            </Typography>

            <Property
                title={t('Reservation days past')}
                tooltip={t('How far back full sync should look for reservations.')}
            >
                <TextField
                    id={'reservation-days-past'}
                    size={'small'}
                    value={ohipConfig.pollerConfig?.fullSync?.reservationDaysPast || ''}
                    onChange={(e) => updateFullSyncConfig('reservationDaysPast', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Property
                title={t('Reservation days future')}
                tooltip={t('How far into the future full sync should look for reservations.')}
            >
                <TextField
                    id={'reservation-days-future'}
                    size={'small'}
                    value={ohipConfig.pollerConfig?.fullSync?.reservationDaysFuture || ''}
                    onChange={(e) => updateFullSyncConfig('reservationDaysFuture', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Property
                title={t('Stale timeout minutes')}
                tooltip={t('Minutes before a full sync job is treated as stale.')}
            >
                <TextField
                    id={'stale-timeout-minutes'}
                    size={'small'}
                    value={ohipConfig.pollerConfig?.fullSync?.staleTimeoutMinutes || ''}
                    onChange={(e) => updateFullSyncConfig('staleTimeoutMinutes', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Typography textAlign={'start'} sx={{mt: 2}}>
                {t('Daily sync')}
            </Typography>

            <Property
                title={t('Enabled')}
                tooltip={t('Turns scheduled daily sync on or off.')}
            >
                <Switch
                    id={'daily-sync-scheduled'}
                    checked={ohipConfig.pollerConfig?.daySync?.enabled || false}
                    onChange={(e) => updateDailyConfig('enabled', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Reservation days future')}
                tooltip={t('How far into the future daily sync should look for reservations.')}
            >
                <TextField
                    id={'reservation-days-future-daily'}
                    size={'small'}
                    value={ohipConfig.pollerConfig?.daySync?.reservationDaysFuture || ''}
                    onChange={(e) => updateDailyConfig('reservationDaysFuture', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Property
                title={t('Stale timeout minutes')}
                tooltip={t('Minutes before a daily sync job is treated as stale.')}
            >
                <TextField
                    id={'stale-timeout-minutes'}
                    size={'small'}
                    value={ohipConfig.pollerConfig?.daySync?.staleTimeoutMinutes || ''}
                    onChange={(e) => updateDailyConfig('staleTimeoutMinutes', parseInt(e.target.value))}
                    fullWidth
                />
            </Property>

            <Typography textAlign={'start'} sx={{mt: 2}}>
                {t('Quick sync')}
            </Typography>

            <Property
                title={t('Enabled')}
                tooltip={t('Turns scheduled quick sync on or off.')}
            >
                <Switch
                    id={'quick-sync-scheduled'}
                    checked={ohipConfig.pollerConfig?.quickSync?.enabled || false}
                    onChange={(e) => updateQuickConfig('enabled', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Stale timeout minutes')}
                tooltip={t('Minutes before a quick sync job is treated as stale.')}
            >
                <TextField
                    id={'quick-stale-timeout-minutes'}
                    size={'small'}
                    value={ohipConfig.pollerConfig?.quickSync?.staleTimeoutMinutes || ''}
                    onChange={(e) => updateQuickConfig('staleTimeoutMinutes', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Property
                title={t('Sync period minutes')}
                tooltip={t('Minutes between scheduled quick sync runs.')}
            >
                <TextField
                    id={'quick-sync-period-minutes'}
                    size={'small'}
                    value={ohipConfig.pollerConfig?.quickSync?.syncPeriodMinutes || ''}
                    onChange={(e) => updateQuickConfig('syncPeriodMinutes', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

        </AdditionalProperties>
    )

    const dataProcessingRules = (
        <AdditionalProperties title={t('Data processing rules')} variant={'outlined'}>

            <Property
                title={t('Accommodation sector')}
                tooltip={t('Sector where imported accommodation reservations and resources should be mapped.')}
            >
                <Autocomplete
                    id={'sectors'}
                    options={sectors}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={sectors.find(s => s.id === ohipConfig.dataProcessingRules?.accommodationSectorId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('accommodationSectorId', value?.id)
                    }}
                />
            </Property>

            <Property
                title={t('Adults age category')}
                tooltip={t('Age category used for adult guest counts imported from OHIP.')}
            >
                <Autocomplete
                    id={'adults-age-categories'}
                    options={ageCategories}
                    getOptionLabel={(option: AgeCategory) => option.name || ''}
                    value={ageCategories.find(s => s.id === ohipConfig.dataProcessingRules?.adultsAgeCategoryId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('adultsAgeCategoryId', value?.id || undefined)
                    }}
                />
            </Property>

            <Property
                title={t('Children age category')}
                tooltip={t('Age category used for child guest counts imported from OHIP.')}
            >
                <Autocomplete
                    id={'children-age-categories'}
                    options={ageCategories}
                    getOptionLabel={(option: AgeCategory) => option.name || ''}
                    value={ageCategories.find(s => s.id === ohipConfig.dataProcessingRules?.childrenAgeCategoryId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('childrenAgeCategoryId', value?.id || undefined)
                    }}
                />
            </Property>

            <Property
                title={t('Teenager age category')}
                tooltip={t('Age category used for teenager guest counts imported from OHIP.')}
            >
                <Autocomplete
                    id={'teenager-age-categories'}
                    options={ageCategories}
                    getOptionLabel={(option: AgeCategory) => option.name || ''}
                    value={ageCategories.find(s => s.id === ohipConfig.dataProcessingRules?.teenagerAgeCategoryId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('teenagerAgeCategoryId', value?.id || undefined)
                    }}
                />
            </Property>

            <Property
                title={t('Infant age category')}
                tooltip={t('Age category used for infant guest counts imported from OHIP.')}
            >
                <Autocomplete
                    id={'infant-age-categories'}
                    options={ageCategories}
                    getOptionLabel={(option: AgeCategory) => option.name || ''}
                    value={ageCategories.find(s => s.id === ohipConfig.dataProcessingRules?.infantAgeCategoryId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('infantAgeCategoryId', value?.id || undefined)
                    }}
                />
            </Property>

            <Property
                title={t('Auto create resource for a room if not found')}
                tooltip={t('Creates missing room resources automatically when imported OHIP data references an unknown room.')}
            >
                <Switch
                    id={'auto-create-resource-if-not-found'}
                    checked={ohipConfig.dataProcessingRules?.autoCreateResourceIfNotFound}
                    onChange={(e) => updateDataProcessingRules('autoCreateResourceIfNotFound', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Auto create service a room if not found')}
                tooltip={t('Creates missing services automatically when imported OHIP rate-plan data references an unknown service.')}
            >
                <Switch
                    id={'auto-create-service-if-not-found'}
                    checked={ohipConfig.dataProcessingRules?.autoCreateServiceForRatePlanIfNotFound}
                    onChange={(e) => updateDataProcessingRules('autoCreateServiceForRatePlanIfNotFound', e.target.checked)}
                />
            </Property>

        </AdditionalProperties>
    )

    const configurationSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Property ID / Name (optional, multiproperty)')}
                    tooltip={t('Optional property identifier assigned to all data imported through this connector.')}
                >
                    <Stack
                        width={'100%'}
                        gap={1}
                    >
                        <Alert
                            variant={'outlined'}
                            severity={'info'}
                            sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
                        >
                            {t('Put property id or name here. If set, all incoming data via this connector (profiles, reservations...) will have this property id. You will be able to filter data by this property id in the future')}
                        </Alert>

                        <Alert
                            variant={'outlined'}
                            severity={'error'}
                            sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
                        >
                            {t('Do not change this value if you are not sure what you are doing! It may cause data inconsistency. If you want to change it, please first delete all data from this connector')}
                        </Alert>

                        <TextField
                            id={'name'}
                            size={'small'}
                            value={ohipConfig.propertyId || ''}
                            onChange={(e) => updateField('propertyId', e.target.value)}
                            fullWidth
                            required
                        />
                    </Stack>

                </Property>

                {pollerConfig}
                {dataProcessingRules}
            </Stack>
        </Stack>
    )

    //endregion UI

    return (
        <Stack
            sx={{
                width: '100%',
                gap: 6,
            }}
        >
            {credentialsSection}
            <Divider/>
            {configurationSection}
        </Stack>
    )

}

export default OhipConfigForm;
